import { Content, DefaultPage } from '@shared/layout/DefaultPage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useIonRouter, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react'
import { BlueYellowButton } from '@/shared/ui/BlueYellowButton'
import { TimerCircle } from '@/shared/ui/TimerCircle'
import { ExecutionInput } from './ExecutinoInput'
import { Header } from './Header'
import { ROUTES } from '@/shared/constants/routes'
import { parseTimerPresetId } from '../Main/lib/useTimerPresets'
import { CompletionModal } from './ui/CompletionModal'
import { Pause } from 'lucide-react'

const formatSeconds = (seconds: number | undefined) => {
	if (seconds === undefined) return '--:--'
	const minutes = Math.floor(seconds / 60)
	const remSeconds = seconds % 60
	return `${String(minutes).padStart(2, '0')}:${String(remSeconds).padStart(2, '0')}`
}

const clampToMinOne = (value: number) => Math.max(1, value)
const getTimerNow = () => Date.now()

type TimerRuntimePhase = 'work' | 'rest'

interface TimerRuntimeState {
	currentRound: number
	currentPhase: TimerRuntimePhase
	phaseDuration: number
	timeLeft: number
	elapsedSeconds: number
	isRunning: boolean
	isFinished: boolean
}

const buildInitialRuntimeState = (workTime: number): TimerRuntimeState => ({
	currentRound: 1,
	currentPhase: 'work',
	phaseDuration: workTime,
	timeLeft: workTime,
	elapsedSeconds: 0,
	isRunning: true,
	isFinished: false,
})

const moveToNextPhase = (
	state: TimerRuntimeState,
	rounds: number,
	workTime: number,
	restTime: number
): TimerRuntimeState => {
	if (state.currentPhase === 'work') {
		return {
			...state,
			currentPhase: 'rest',
			phaseDuration: restTime,
			timeLeft: restTime,
		}
	}

	const nextRound = state.currentRound + 1
	if (nextRound > rounds) {
		return { ...state, timeLeft: 0, isRunning: false, isFinished: true }
	}

	return {
		...state,
		currentRound: nextRound,
		currentPhase: 'work',
		phaseDuration: workTime,
		timeLeft: workTime,
	}
}

const advanceRuntimeBySeconds = (
	state: TimerRuntimeState,
	elapsedSeconds: number,
	rounds: number,
	workTime: number,
	restTime: number
): TimerRuntimeState => {
	if (!state.isRunning || state.isFinished || elapsedSeconds <= 0) {
		return state
	}

	let nextState = state
	let remainingElapsedSeconds = elapsedSeconds
	let consumedSeconds = 0

	while (remainingElapsedSeconds > 0 && !nextState.isFinished) {
		if (remainingElapsedSeconds < nextState.timeLeft) {
			consumedSeconds += remainingElapsedSeconds
			return {
				...nextState,
				elapsedSeconds: nextState.elapsedSeconds + consumedSeconds,
				timeLeft: nextState.timeLeft - remainingElapsedSeconds,
			}
		}

		remainingElapsedSeconds -= nextState.timeLeft
		consumedSeconds += nextState.timeLeft
		nextState = moveToNextPhase(nextState, rounds, workTime, restTime)
	}

	return {
		...nextState,
		elapsedSeconds: nextState.elapsedSeconds + consumedSeconds,
	}
}

const getRemainingTotalTime = (
	state: TimerRuntimeState,
	rounds: number,
	workTime: number,
	restTime: number
) => {
	if (state.isFinished) {
		return 0
	}

	if (state.currentPhase === 'work') {
		return state.timeLeft + restTime + (rounds - state.currentRound) * (workTime + restTime)
	}

	return state.timeLeft + (rounds - state.currentRound) * (workTime + restTime)
}

export const TimerExecutionPage = () => {
	const { presetId } = useParams<{ presetId?: string }>()
	const preset = useMemo(() => parseTimerPresetId(presetId ?? ''), [presetId])
	const nav = useIonRouter()
	const rounds = clampToMinOne(preset?.rounds ?? 9)
	const workTime = clampToMinOne(preset?.approach ?? 20)
	const restTime = clampToMinOne(preset?.rest ?? 20)
	const [runtime, setRuntime] = useState<TimerRuntimeState>(() => buildInitialRuntimeState(workTime))
	const [isViewActive, setIsViewActive] = useState(true)
	const lastTickAtRef = useRef<number | null>(null)

	useEffect(() => {
		setRuntime(buildInitialRuntimeState(workTime))
	}, [workTime, restTime, rounds])

	useIonViewDidEnter(() => {
		setIsViewActive(true)
	})

	useIonViewDidLeave(() => {
		setIsViewActive(false)
		setRuntime(prev => (prev.isRunning ? { ...prev, isRunning: false } : prev))
	})

	useEffect(() => {
		if (!runtime.isRunning || runtime.isFinished || !isViewActive) {
			lastTickAtRef.current = null
			return
		}

		lastTickAtRef.current = getTimerNow()
		const interval = setInterval(() => {
			const now = getTimerNow()
			const lastTickAt = lastTickAtRef.current ?? now
			const elapsedWholeSeconds = Math.floor((now - lastTickAt) / 1000)

			if (elapsedWholeSeconds <= 0) {
				return
			}

			lastTickAtRef.current = lastTickAt + elapsedWholeSeconds * 1000
			setRuntime(prev =>
				advanceRuntimeBySeconds(
					prev,
					elapsedWholeSeconds,
					rounds,
					workTime,
					restTime
				)
			)
		}, 250)

		return () => clearInterval(interval)
	}, [runtime.isRunning, runtime.isFinished, rounds, restTime, workTime, isViewActive])

	const adjustCurrentPhase = useCallback((delta: number) => {
		setRuntime(prev => {
			if (prev.isFinished) {
				return prev
			}

			const updatedDuration = clampToMinOne(prev.phaseDuration + delta)
			const appliedDelta = updatedDuration - prev.phaseDuration

			if (appliedDelta === 0) {
				return prev
			}

			return {
				...prev,
				phaseDuration: updatedDuration,
				timeLeft: clampToMinOne(prev.timeLeft + appliedDelta),
			}
		})
	}, [])

	const restartTimer = useCallback(() => {
		setRuntime(buildInitialRuntimeState(workTime))
	}, [workTime])

	const remainingTotalTime = useMemo(
		() => getRemainingTotalTime(runtime, rounds, workTime, restTime),
		[runtime, rounds, workTime, restTime]
	)
	const isRest = runtime.currentPhase === 'rest'
	const isFinalCountdown = runtime.isRunning && runtime.timeLeft > 0 && runtime.timeLeft <= 3
	const actionButtonLabel = runtime.isRunning ? 'Пауза' : runtime.isFinished ? 'Завершено' : 'Продолжить'
	const completionTotalTime = formatSeconds(runtime.elapsedSeconds)

	return (
		<DefaultPage noPadding>
			<Content className='app-bottom-nav-content' scrollY={false}>
				<Header onFinish={() => nav.push(ROUTES.overall.main)} />
				<div className='mt-2 flex h-full min-h-0 flex-col px-4 pb-[calc(var(--app-bottom-nav-reserve)+26px)]'>
					<div className='mx-auto flex min-w-[220px] flex-col items-center rounded-[26px] border border-[#E8E8E8] bg-[#FAFAFA] px-[56px] py-2.5'>
						<span className='text-[17px] text-center font-medium text-[#7E7E7E]'>
							Общее время
						</span>
						<span className='mt-[-6px] text-center text-[44px] font-semibold leading-none tracking-[-0.03em] tabular-nums'>
							{formatSeconds(remainingTotalTime)}
						</span>
					</div>

					<div className='mt-8 flex justify-center'>
						<TimerCircle
							currentPhaseDuration={runtime.phaseDuration}
							currentRound={runtime.currentRound}
							phaseLabel={isRest ? 'Отдых' : 'Подход'}
							radius={164}
							remainingTime={runtime.timeLeft}
							rounds={rounds}
							strokeWidth={10}
							isFinalCountdown={isFinalCountdown}
						/>
					</div>
					<div className='mt-auto flex flex-col gap-3 pt-4'>
						<ExecutionInput
							className='w-full'
							onIncrease={() => adjustCurrentPhase(10)}
							onDecrease={() => adjustCurrentPhase(-10)}
						/>

						<BlueYellowButton
							onClick={() => setRuntime(prev => ({ ...prev, isRunning: !prev.isRunning }))}
							className='h-[72px] rounded-[24px] disabled:bg-[#AEB4FF] disabled:opacity-80'
							disabled={runtime.isFinished}>
							<span className='flex items-center justify-center gap-3 text-[22px] font-medium tracking-[-0.02em] text-white'>
								{runtime.isRunning && <Pause aria-hidden='true' className='size-7 fill-white stroke-white' />}
								{actionButtonLabel}
							</span>
						</BlueYellowButton>
					</div>
				</div>
			</Content>

			<CompletionModal
				isOpen={runtime.isFinished && isViewActive}
				rounds={rounds}
				totalTimeLabel={completionTotalTime}
				onRepeat={restartTimer}
				onCloseToMain={() => nav.push(ROUTES.overall.main)}
			/>
		</DefaultPage>
	)
}
